/**
 * The hand-off between signup's two pages.
 *
 * Step 1 (`https://lifekees.com/signup/`, built from `apps/web/site/`) runs
 * Cloudflare Turnstile and receives a ticket; step 2 (`routes/Register.svelte`,
 * inside the vault app at `https://vault.lifekees.com/app/register`) takes the
 * master password and the Secret Key. They are separate documents on separate
 * **origins**, and that is the security property the split exists for:
 *
 *  - a separate document means Cloudflare's script dies with step 1's page at
 *    the navigation, before any secret is typed;
 *  - a separate *origin* means that while it lived it could not reach anything
 *    of the vault's. Same-origin would not have been enough: a script on the
 *    vault's origin could read its IndexedDB (encrypted vaults, and a Secret
 *    Key the user chose to remember), rewrite its Cache Storage (which the
 *    service worker serves the app's own JavaScript from — a persistent
 *    takeover of every later visit), and `window.open` an app page and read its
 *    DOM as the password is typed.
 *
 * Step 1's origin is the *parent* of the vault's, which is what makes the third
 * boundary hold as well: WebAuthn lets a page use an RP ID equal to its own
 * host or a registrable-domain suffix of it, so `lifekees.com` cannot request a
 * passkey for the vault's RP ID, `vault.lifekees.com`, in any browser. (When
 * step 1 sat on `signup.lifekees.com` under an RP ID of `lifekees.com` it
 * could, and only Chromium's Permissions-Policy stood in the way.) The server
 * refuses to open signup if `SIGNUP_ORIGIN` could reach the vault's passkeys.
 *
 * One registrable-domain boundary is still shared: browser password managers'
 * same-site matching. See docs/SECURITY.md §15, "Password managers can offer a
 * vault password on the marketing origin".
 *
 * Origins do not share `sessionStorage`, so the ticket crosses in the URL
 * **fragment** of the navigation: `/app/register#signup=<ticket>`. A fragment
 * is never sent to a server and never appears in a `Referer`; step 2 moves it
 * into its own `sessionStorage` at once and strips it from the address bar.
 *
 * This module is imported by *both* sides — the vault app and step 1 — so it
 * imports nothing: it must not be the thread that ties step 1's bundle to the
 * app's. `test/signup-isolation.test.ts` pins step 1's whole module set.
 */

/** `POST /api/v2/signup/ticket`. Pinned against `ROUTES` by a test. */
export const SIGNUP_TICKET_ENDPOINT = '/api/v2/signup/ticket';

/** The fragment parameter the ticket travels in. */
export const TICKET_FRAGMENT_PARAM = 'signup';

/** The app route step 1 hands over to. */
export const REGISTER_PATH = '/app/register';

/** Where step 2 keeps the ticket for the life of the tab (survives a reload). */
export const PENDING_SIGNUP_KEY = 'lk:pending-signup';

/**
 * Treat a ticket as expired this long before the server would, so the password
 * form is not offered for a ticket that will lapse while it is being filled in.
 */
const EXPIRY_MARGIN_SECONDS = 60;

/**
 * Deployment addresses. Committed rather than read from a gitignored `.env`,
 * because none of them is secret and all of them are worth reviewing: the
 * production Turnstile site key is public by design (it is in every page that
 * renders the widget). Each can be overridden at build time with the `VITE_*`
 * variable named beside it.
 *
 * Outside production the key is Cloudflare's documented always-pass test key
 * (developers.cloudflare.com/turnstile/troubleshooting/testing/), which pairs
 * with the test secret `scripts/dev.sh` gives the API — and step 1 is still on
 * an origin of its own: the vault's Vite server starts the marketing site's
 * on the next port up (`DEV_SITE_PORT_OFFSET`, see `vite-dev-site.ts`), so
 * Cloudflare's script never shares an origin with a developer's local vault
 * either.
 */
const env = import.meta.env;
export const TURNSTILE_SITEKEY: string =
  env.VITE_TURNSTILE_SITEKEY || (env.PROD ? '0x4AAAAAAFI_RWFU7yh7NKWW' : '1x00000000000000000000AA');

/** Production: step 1, on the marketing origin. */
export const PROD_SIGNUP_URL = 'https://lifekees.com/signup/';
/** Production: the vault's origin, where step 2 and the API live. */
export const PROD_APP_ORIGIN = 'https://vault.lifekees.com';

/**
 * Development only: the marketing site (and so step 1) is served on the vault's
 * Vite port plus this. Must equal `DEV_SITE_PORT_OFFSET` in `vite-dev-site.ts`
 * (`test/dev-signup-origin.test.ts` pins it).
 */
export const DEV_SITE_PORT_OFFSET = 1;

/** This page's origin with its port moved by `offset`; '' when there is no port. */
function devSibling(offset: number): string {
  const here = globalThis.location;
  const port = Number(here?.port);
  if (!here || !port) return '';
  return `${here.protocol}//${here.hostname}:${port + offset}`;
}

/** Step 1's address, as seen from the app. Always another origin. */
export const SIGNUP_URL: string =
  env.VITE_SIGNUP_URL || (env.PROD ? PROD_SIGNUP_URL : `${devSibling(DEV_SITE_PORT_OFFSET)}/signup/`);
/**
 * The marketing site's landing page, as seen from the vault: the origin step 1
 * is on. For the vault's "About Lifekees" link — before the split the landing
 * page was the app's own `/`, and the vault had no other way back to it.
 */
export const SITE_URL: string = new URL('/', new URL(SIGNUP_URL, globalThis.location?.href ?? PROD_SIGNUP_URL)).href;
/** The vault's origin, as seen from the marketing site (only the site reads it). */
export const APP_ORIGIN: string =
  env.VITE_APP_ORIGIN || (env.PROD ? PROD_APP_ORIGIN : devSibling(-DEV_SITE_PORT_OFFSET));

/** A ticket this client holds, with the two claims it can read out of it. */
export interface PendingSignup {
  ticket: string;
  /** The normalised address the ticket is bound to. */
  email: string;
  /** Unix seconds. */
  expiresAt: number;
}

/**
 * Read the claims out of a ticket (`v1.<base64url JSON>.<mac>`).
 *
 * **Not** a verification — only the server can do that — just enough to show
 * the address and to stop offering the form once the ticket has lapsed. A
 * doctored ticket gets as far as the server's 403.
 */
export function decodeTicket(ticket: string): { email: string; expiresAt: number } | null {
  const parts = ticket.split('.');
  if (parts.length !== 3 || parts[0] !== 'v1' || !parts[1]) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0)),
    );
    const claims = JSON.parse(json) as { e?: unknown; x?: unknown };
    if (typeof claims.e !== 'string' || typeof claims.x !== 'number' || !claims.e.includes('@')) {
      return null;
    }
    return { email: claims.e, expiresAt: claims.x };
  } catch {
    return null;
  }
}

/** The URL step 1 navigates to, carrying `ticket` in the fragment. */
export function handoffUrl(ticket: string, appOrigin: string = APP_ORIGIN): string {
  return `${appOrigin}${REGISTER_PATH}#${TICKET_FRAGMENT_PARAM}=${encodeURIComponent(ticket)}`;
}

interface HandoffDeps {
  location?: Pick<Location, 'hash' | 'pathname' | 'search'>;
  history?: Pick<History, 'replaceState'>;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  /** Unix milliseconds. */
  now?: number;
}

function sessionStore(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Storage disabled: the ticket still works for this page load, it just
    // does not survive a reload.
    return null;
  }
}

/**
 * The ticket, when `sessionStorage` cannot hold it (storage disabled). Lives
 * as long as this page load, which is as long as the screen that needs it.
 */
let inMemory: PendingSignup | null = null;

/**
 * Move a ticket out of the URL fragment, into this tab's keeping.
 *
 * **Must run before the router is constructed.** The router's first commit
 * rewrites the address bar to the matched path, dropping the fragment — so
 * by the time `Register.svelte` mounts, the ticket would be gone and the user
 * sent back to step 1 in a loop. `main.ts` therefore imports
 * `lib/signup-handoff.ts`, which calls this, as its very first import.
 *
 * The fragment is removed whatever it holds: a ticket left in the address bar
 * is one screenshot or one copied link away from somebody else.
 */
export function adoptFragmentTicket(deps: HandoffDeps = {}): void {
  const location = deps.location ?? globalThis.location;
  const history = deps.history ?? globalThis.history;
  const storage = deps.storage === undefined ? sessionStore() : deps.storage;

  const hash = location?.hash ?? '';
  if (hash.length <= 1) return;
  const raw = new URLSearchParams(hash.slice(1)).get(TICKET_FRAGMENT_PARAM);
  if (raw === null) return;

  history?.replaceState({}, '', location.pathname + location.search);
  const claims = decodeTicket(raw);
  if (!claims) return;
  const pending: PendingSignup = { ticket: raw, ...claims };
  inMemory = pending;
  try {
    storage?.setItem(PENDING_SIGNUP_KEY, JSON.stringify(pending));
  } catch {
    /* kept in memory; see sessionStore() */
  }
}

/**
 * The ticket this tab may register with, or `null`.
 *
 * Adopts one from the fragment if it is (still) there, then reads the one this
 * tab keeps — `sessionStorage`, so it survives a reload, else memory. An
 * expired or unreadable ticket is discarded and reported as none.
 */
export function takePendingSignup(deps: HandoffDeps = {}): PendingSignup | null {
  const storage = deps.storage === undefined ? sessionStore() : deps.storage;
  const now = Math.floor((deps.now ?? Date.now()) / 1000);
  adoptFragmentTicket(deps);

  let pending: PendingSignup | null = null;
  try {
    const stored = storage?.getItem(PENDING_SIGNUP_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<PendingSignup>;
      const claims = typeof parsed.ticket === 'string' ? decodeTicket(parsed.ticket) : null;
      if (claims) pending = { ticket: parsed.ticket as string, ...claims };
    }
  } catch {
    pending = null;
  }
  pending ??= inMemory;

  if (pending && pending.expiresAt - EXPIRY_MARGIN_SECONDS <= now) {
    clearPendingSignup(storage);
    return null;
  }
  return pending;
}

/** Forget the ticket: after a registration, or when the user starts over. */
export function clearPendingSignup(
  storage: Pick<Storage, 'removeItem'> | null = sessionStore(),
): void {
  inMemory = null;
  try {
    storage?.removeItem(PENDING_SIGNUP_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Leave for step 1. A **full** navigation, never a router push: step 1 is a
 * different document (and, in production, a different origin), and the app's
 * router cannot render it.
 *
 * Indirected through this object so the component tests can observe it —
 * jsdom does not implement navigation.
 */
export const signupNavigation = {
  assign(url: string): void {
    globalThis.location.assign(url);
  },
};

/** Go to step 1, forgetting any ticket this tab held. */
export function startSignup(): void {
  clearPendingSignup();
  signupNavigation.assign(SIGNUP_URL);
}
